// Custom hash router for the S7 Email Builder app shell. Per
// spec/tech-arch.md §6 + spec/build-plan.md P-023 row.
//
// **Six-route table.** `#/` (Home), `#/templates` + `#/templates/:id`
// (Template list + editor), `#/emails` + `#/emails/:id` (Email list +
// editor), `#/library` (Block library) — verbatim from Tech Arch §6.
// Order matters: the dynamic `:id` patterns sit AFTER their matching
// list patterns (e.g. `#/templates/:id` after `#/templates`) so a
// literal `#/templates` hash matches the list, not the editor with a
// captured `id` of `"templates"`. Only the `home` pattern (`^#/$`)
// is purely static; the others all share that ordering invariant.
//
// **`compilePattern` regex helper (~10 LOC, hand-rolled).** Per the
// P-023 build-plan row decision surface ("hand-roll a small regex
// helper, no library needed"). Each `:name` segment becomes a named
// capture (`(?<name>[^/]+)`) — named captures over positional groups
// because the only param across the V1 route table is `:id`, and
// named lookup lets `parseHash` read `m.groups?.id` directly without
// pairing positional indices to a paramNames array. Patterns compile
// once at module load (`new RegExp(...)`); match many times. `#/`
// and `/` are not regex metachars, so no escaping is needed for the
// V1 patterns.
//
// **`useRoute()` return shape.** Discriminated union by `name`: `{
// name: 'home' | 'templates' | 'template-editor'
// | 'emails' | 'email-editor' | 'library' | 'unknown'; params: { id?:
// string } }`. Picked over a generic `{ pathname: string; params:
// Record<string, string> }` because the union pins the route name set
// in TypeScript, lets `<RouterOutlet />` switch exhaustively on
// `name`, and gives consumers a strongly-typed `route.name` to narrow
// on. The `unknown` sentinel is the parser's response to an
// unrecognized hash (see next note).
//
// **Empty hash + unknown route handling.** Empty hash (`''`) is
// normalized to `#/` at parse time — non-mutating, idempotent; users
// arriving at the canonical `/` URL keep a clean URL bar until they
// navigate. Unrecognized hashes return `{ name: 'unknown', params: {}
// }`; `<RouterOutlet />` then drives `window.location.hash = '#/'`
// in a useEffect, which fires `hashchange` → `useRoute()` re-parses →
// the visible URL bar and rendered screen both normalize to Home.
// Picked over a silent home fallback because the QA item ("Unknown
// routes redirect to `#/`") reads literally — the user-facing URL
// must reflect the canonical route after a redirect.
//
// **`<RouterOutlet />` lives here, not in `<App />`.** Encapsulates
// the route-to-screen mapping inside this file, keeps `<App />` as a
// thin layout chrome + slot. The alternative (inline `switch` in
// `<App />`) would couple the App shell to the screen module set,
// requiring an `<App />` edit on every new screen.
//
// **Focus management on screen transitions (P-036).** When the route
// changes, focus moves to `<main id="main-content" tabIndex={-1}>` so
// keyboard + screen-reader users land in the new screen content
// without re-traversing the header on every navigation. Skipped on
// initial mount: the page-load focus belongs to the URL bar / first
// tab stop, not to a programmatic-focus seam. Skipped while the route
// is `'unknown'` since the redirect-to-home effect will fire another
// route change a tick later — focusing twice would steal focus during
// the redirect race.

import { useEffect, useRef } from 'preact/hooks'

import { useRoute } from './hooks/use-route'
import { BlockGalleryScreen } from './screens/block-gallery'
import { BlockPreviewScreen } from './screens/block-preview'
import { CCCallbackScreen } from './screens/cc-callback'
import { EmailEditorScreen } from './screens/email-editor'
import { EmailListScreen } from './screens/email-list'
import { HomeScreen } from './screens/home'
import { LibraryScreen } from './screens/library'
import { SettingsScreen } from './screens/settings'
import { TemplateEditorScreen } from './screens/template-editor'
import { TemplateListScreen } from './screens/template-list'

export type RouteName =
  | 'home'
  | 'templates'
  | 'template-editor'
  | 'emails'
  | 'email-editor'
  | 'library'
  | 'blocks'
  | 'block-preview'
  | 'settings'
  | 'cc-callback'
  | 'unknown'

export interface Route {
  readonly name: RouteName
  readonly params: { readonly id?: string }
}

interface CompiledRoute {
  readonly name: Exclude<RouteName, 'unknown'>
  readonly regex: RegExp
}

function compilePattern(pattern: string): RegExp {
  return new RegExp(`^${pattern.replace(/:(\w+)/g, '(?<$1>[^/]+)')}$`)
}

const ROUTES: readonly CompiledRoute[] = [
  { name: 'home', regex: compilePattern('#/') },
  { name: 'templates', regex: compilePattern('#/templates') },
  { name: 'template-editor', regex: compilePattern('#/templates/:id') },
  { name: 'emails', regex: compilePattern('#/emails') },
  { name: 'email-editor', regex: compilePattern('#/emails/:id') },
  { name: 'library', regex: compilePattern('#/library') },
  { name: 'blocks', regex: compilePattern('#/blocks') },
  { name: 'block-preview', regex: compilePattern('#/blocks/:id') },
  { name: 'settings', regex: compilePattern('#/settings') },
  { name: 'cc-callback', regex: compilePattern('#/auth/cc-callback') },
]

export function parseHash(hash: string): Route {
  // Strip any `?query` from the hash before route matching. Hash routers
  // see CC OAuth's callback as `#/auth/cc-callback?code=X&state=Y`; the
  // route pattern matches the path only — the screen reads the params
  // off `window.location.href` directly. Added at P-047 / D-142 for the
  // cc-callback route; benign for the existing routes (none use query
  // strings, so stripping is a no-op).
  const withoutQuery = hash.split('?')[0] ?? ''
  const normalized = withoutQuery === '' ? '#/' : withoutQuery
  for (const r of ROUTES) {
    const m = r.regex.exec(normalized)
    if (m === null) continue
    const id = m.groups?.['id']
    return id === undefined ? { name: r.name, params: {} } : { name: r.name, params: { id } }
  }
  return { name: 'unknown', params: {} }
}

export function RouterOutlet() {
  const route = useRoute()
  const initialMountRef = useRef(true)
  useEffect(() => {
    if (route.name === 'unknown') window.location.hash = '#/'
  }, [route.name])
  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false
      return
    }
    if (route.name === 'unknown') return
    document.getElementById('main-content')?.focus()
  }, [route.name, route.params.id])
  switch (route.name) {
    case 'home':
      return <HomeScreen />
    case 'templates':
      return <TemplateListScreen />
    case 'template-editor':
      return <TemplateEditorScreen />
    case 'emails':
      return <EmailListScreen />
    case 'email-editor':
      return <EmailEditorScreen />
    case 'library':
      return <LibraryScreen />
    case 'blocks':
      return <BlockGalleryScreen />
    case 'block-preview':
      return <BlockPreviewScreen />
    case 'settings':
      return <SettingsScreen />
    case 'cc-callback':
      return <CCCallbackScreen />
    case 'unknown':
      return null
  }
}
